import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { CtaBanner } from '@/components/sections/CtaBanner';
import { StoreBadges } from '@/components/StoreBadges';

const DATE_LOCALES = { en: 'en-US', pt: 'pt-PT', es: 'es-ES', fr: 'fr-FR', de: 'de-DE' };

export default function GuideArticle() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const guides = t('guides', { returnObjects: true });
  const guide = guides.find((g) => g.slug === slug);
  if (!guide) return <Navigate to="/guides" replace />;

  const url = `https://copy.click/guides/${guide.slug}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: guide.title,
        description: guide.description,
        datePublished: guide.date,
        dateModified: guide.date,
        mainEntityOfPage: url,
        image: 'https://copy.click/og-image.png',
        author: { '@type': 'Organization', name: 'Copy.Click', url: 'https://copy.click/' },
        publisher: { '@type': 'Organization', name: 'Copy.Click', logo: { '@type': 'ImageObject', url: 'https://copy.click/logo-mark.svg' } },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://copy.click/' },
          { '@type': 'ListItem', position: 2, name: 'Guides', item: 'https://copy.click/guides' },
          { '@type': 'ListItem', position: 3, name: guide.title, item: url },
        ],
      },
    ],
  };

  const related = guides.filter((g) => g.slug !== guide.slug).slice(0, 3);
  const dateLocale = DATE_LOCALES[i18n.language] || 'en-US';

  return (
    <>
      <Seo title={guide.metaTitle} description={guide.description} jsonLd={jsonLd} />

      <article className="relative overflow-hidden px-6 pb-24 pt-32 lg:px-8 sm:pt-40">
        <div className="pointer-events-none absolute right-0 top-24 -z-10 h-96 w-96 rounded-full bg-brand/10 blur-[130px]" />
        <div className="mx-auto max-w-6xl">
          <Link to="/guides" data-testid="guide-back" className="inline-flex items-center gap-2 text-sm text-white/50 transition-colors hover:text-brand">
            <ArrowLeft className="h-4 w-4" /> {t('guidesPage.allGuides')}
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
            <span className="rounded-full bg-brand/15 px-3 py-1 font-semibold text-brand">{guide.category}</span>
            <span className="flex items-center gap-1 text-white/40"><Clock className="h-3.5 w-3.5" /> {guide.readTime} {t('common.minRead')}</span>
            <span className="text-white/30">{t('guidesPage.updated')} {new Date(guide.date).toLocaleDateString(dateLocale, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          </div>

          <h1 className="mt-5 max-w-4xl font-display text-3xl font-bold leading-[1.15] tracking-tight text-white sm:text-5xl">{guide.title}</h1>

          <div className="mt-10 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              {guide.intro.map((p, i) => (
                <p key={i} className="mb-5 text-base leading-relaxed text-white/70 sm:text-lg">{p}</p>
              ))}
              {guide.sections.map((s) => (
                <section key={s.h2} className="mt-10">
                  <h2 className="font-display text-xl font-bold text-white sm:text-2xl">{s.h2}</h2>
                  {(s.paras || []).map((p, i) => (
                    <p key={i} className="mt-4 text-base leading-relaxed text-white/60">{p}</p>
                  ))}
                  {s.list && (
                    <ul className="mt-5 space-y-3">
                      {s.list.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-base leading-relaxed text-white/60">
                          <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-brand" /> {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              <div className="mt-14 rounded-3xl border border-brand/20 bg-[#001826]/70 p-8 text-center lg:text-left">
                <h2 className="font-display text-xl font-bold text-white">{t('guidesPage.practiceTitle')}</h2>
                <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-white/55 lg:mx-0">{t('guidesPage.practiceSubtitle')}</p>
                <div className="mt-6"><StoreBadges /></div>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <p className="text-xs font-semibold uppercase tracking-wider text-white/40">{t('guidesPage.keepReading')}</p>
                <div className="mt-4 space-y-4">
                  {related.map((g) => (
                    <Link key={g.slug} to={`/guides/${g.slug}`} data-testid="guide-related" className="group block rounded-2xl border border-white/10 bg-surface/50 p-5 transition-colors hover:border-brand/40">
                      <span className="text-[11px] font-semibold text-brand">{g.category}</span>
                      <p className="mt-1.5 font-display text-sm font-semibold leading-snug text-white">{g.title}</p>
                      <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-brand">{t('common.read')} <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" /></span>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <CtaBanner />
    </>
  );
}
